import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import { MathThemeContext } from "@/components/expression/theme";
import { hintLightTheme } from "@/components/expression/theme/corca-themes";
import { createWidgetMap } from "@/components/tiptap/utils/widgetRoot";
import { RootStoreContext } from "@/store/RootStoreProvider";

import { PhantomHintWrapper } from "./PhantomHintWrapper";

import type { WidgetMap } from "@/components/tiptap/utils/widgetRoot";
import type { DocumentModel } from "@/models/DocumentModel";
import type { RootStore } from "@/store";
import type { NodeJson } from "@corca/data";

export type PhantomHintOptions = {
  doc: DocumentModel;
  rootStore: RootStore;
};

type PluginState = {
  pos: number;
  decorations: DecorationSet | null;
  widgetMap: WidgetMap;
};

/**
 * A hint to render: the preview `node` and the document `pos` to place it at. A single-element
 * list is the usual "= result" hint at the cursor; multiple entries preview a formula's result in
 * every empty data cell of a table row at once.
 */
export type PhantomHintItem = { pos: number; node: NodeJson };

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    phantomHint: {
      /** Render one hint per item, or clear all hints when passed `null`. */
      setPhantomHints: (items: PhantomHintItem[] | null) => ReturnType;
    };
  }
}

const pluginKey = new PluginKey<PluginState>("phantomHint");

export const PhantomHint = Extension.create<PhantomHintOptions>({
  name: "phantomHint",

  addProseMirrorPlugins() {
    const { doc, rootStore } = this.options;

    return [
      new Plugin<PluginState>({
        key: pluginKey,
        state: {
          init: () => ({ decorations: null, pos: -1, widgetMap: createWidgetMap() }),
          apply(tr, prev) {
            const { head } = tr.selection;
            let state = prev;
            const items = tr.getMeta("phantomHint") as PhantomHintItem[] | null | undefined;

            if (items === null) {
              state = { pos: -1, decorations: null, widgetMap: prev.widgetMap };
              return state;
            }

            if (prev.pos !== head && items === undefined) {
              // If the position has changed and there's no inline suggestion, clear the state
              return { pos: -1, decorations: null, widgetMap: prev.widgetMap };
            }

            if (items === undefined) {
              return state;
            }

            const { mountWidgetRoot, unmountWidgetRoot } = prev.widgetMap;

            const makeWidget = (pos: number, node: NodeJson) =>
              Decoration.widget(
                pos,
                (view, getPos) => {
                  const div = mountWidgetRoot(
                    <RootStoreContext.Provider value={rootStore}>
                      <MathThemeContext.Provider value={hintLightTheme}>
                        <PhantomHintWrapper
                          node={node}
                          doc={doc}
                          getPos={getPos}
                          pmState={view.state}
                        />
                      </MathThemeContext.Provider>
                    </RootStoreContext.Provider>,
                  );
                  // The line placeholder hides behind this class (theme.css).
                  div.className = "inline phantom-hint";
                  return div;
                },
                {
                  destroy: unmountWidgetRoot,
                },
              );

            // Wrapper highlighting is driven by the observable `editor.highlightedWrapperIds`
            // (read in WrapperNode), not by decorations here.
            const decorations = DecorationSet.create(
              tr.doc,
              items.map((item) => makeWidget(item.pos, item.node)),
            );

            // Track the cursor head so a later plain cursor move (no meta) clears the hints.
            state = { pos: head, decorations, widgetMap: prev.widgetMap };

            return state;
          },
        },
        props: {
          decorations(state) {
            if (!doc.isFocused) {
              return null;
            }

            return this.getState(state)?.decorations ?? null;
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      setPhantomHints:
        (items: PhantomHintItem[] | null) =>
        ({ commands }) => {
          return commands.setMeta("phantomHint", items);
        },
    };
  },
});
